import React, { useEffect, useState } from 'react';
import apiClient from '../config/api';
import Navbar from '../components/Navbar';
import { styles as layoutStyles } from '../styles/newstyles';

const RestrictedUsers = ({ onLogout, isAdmin, username }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/tools/users');
      setUsers(res.data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = users.filter((u) =>
    u.Username?.toLowerCase().includes(query.trim().toLowerCase())
  );

  useEffect(() => {
    load();
  }, []);

  const toggleRestricted = async (u) => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/tools/users/${encodeURIComponent(u.Username)}/restricted`, {
        restricted: !u.Restricted,
      });
      await load();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update user.');
    } finally {
      setSaving(false);
    }
  };

  const tableStyles = {
    wrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', borderRadius: 14, boxShadow: '0 8px 18px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 520 },
    th: { textAlign: 'left', padding: '12px 10px', fontSize: 13, color: '#374151', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' },
    td: { padding: '12px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f1f5f9' },
    empty: { textAlign: 'center', padding: 14, color: '#6b7280' },
    toggle: {
      position: 'relative',
      width: 42,
      height: 22,
      borderRadius: 999,
      border: '1px solid #d1d5db',
      background: '#e5e7eb',
      cursor: 'pointer',
      transition: 'all 120ms ease',
    },
    knob: (on) => ({
      position: 'absolute',
      top: 2,
      left: on ? 20 : 2,
      width: 18,
      height: 18,
      borderRadius: '50%',
      background: on ? '#1f6feb' : '#fff',
      border: '1px solid #cbd5e1',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      transition: 'all 120ms ease',
    }),
  };

  const pageStyles = {
    error: {
      marginTop: 12,
      padding: '10px 12px',
      borderRadius: 8,
      background: '#fef2f2',
      border: '1px solid #fecdd3',
      color: '#b91c1c',
      position: 'relative',
    },
    errorClose: {
      position: 'absolute',
      top: 8,
      right: 10,
      border: 'none',
      background: 'transparent',
      color: '#b91c1c',
      fontSize: 16,
      cursor: 'pointer',
      fontWeight: 700,
      lineHeight: 1,
    },
  };

  return (
    <div style={layoutStyles.mainContainer}>
      <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />
      <div style={{ maxWidth: 1000, margin: '-40px auto 0', padding: '12px' }}>
        <h1 style={{ margin: 0, color: '#023e8a' }}>User Access</h1>
        <p style={{ margin: '6px 0 16px', color: '#374151' }}>
          Toggle “Restricted” to grant access to restricted documents. Restricted admins can view all counties; restricted non-admins can view restricted documents for their assigned county only.
        </p>

        <div style={{ marginBottom: 12 }}>
          <input
            type="search"
            placeholder="Search by username"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              maxWidth: 320,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid #d1d5db',
              fontSize: 14,
            }}
          />
        </div>

        {error && (
          <div style={pageStyles.error}>
            <button type="button" aria-label="Dismiss" onClick={() => setError(null)} style={pageStyles.errorClose}>
              ×
            </button>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 12 }}>Loading...</div>
        ) : (
          <div style={tableStyles.wrap}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Username</th>
                  <th style={tableStyles.th}>Admin</th>
                  <th style={tableStyles.th}>Verified</th>
                  <th style={tableStyles.th}>Restricted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={4} style={tableStyles.empty}>No users found.</td></tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.Username}>
                      <td style={tableStyles.td}>{u.Username}</td>
                      <td style={tableStyles.td}>{u.IsAdmin ? 'Yes' : 'No'}</td>
                      <td style={tableStyles.td}>{u.isVerified ? 'Yes' : 'No'}</td>
                      <td style={tableStyles.td}>
                        <button
                          type="button"
                          aria-label={`Set restricted for ${u.Username}`}
                          onClick={() => toggleRestricted(u)}
                          disabled={saving}
                          style={{
                            ...tableStyles.toggle,
                            background: u.Restricted ? '#dbeafe' : '#e5e7eb',
                            borderColor: u.Restricted ? '#93c5fd' : '#d1d5db',
                          }}
                        >
                          <span style={tableStyles.knob(!!u.Restricted)} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestrictedUsers;
